/**
 * @sazeo/sdk — Banner read hook for tenant-facing apps.
 *
 * The banners table lives in the Platform Admin Supabase project. Tenant-app,
 * marketing, hr-app, intranet, and employee-portal all read banners via the
 * public Edge Function `banners-active` (no auth, edge-cached for 60s).
 *
 * For admin CRUD, see @sazeo/sdk-admin → hooks-banners.
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';

export const BANNER_SLOTS = [
  'marketing.hero',
  'marketing.hr-broker',
  'marketing.footer',
  'intranet.login',
  'tenant.workspace.global',
  'tenant.dashboard.top',
  'hr.landing.hero',
  'hr.candidate.dashboard',
  'hr.company.dashboard',
  'admin.dashboard.top',
  'portal.dashboard.top',
] as const;
export type BannerSlot = (typeof BANNER_SLOTS)[number];

export type BannerVariant = 'promo' | 'upgrade' | 'system' | 'announcement';
export type BannerAudience = 'all' | 'free' | 'pro' | 'enterprise' | 'trial';

export interface ActiveBanner {
  id: string;
  slot: BannerSlot;
  title: string;
  body: string | null;
  image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  variant: BannerVariant;
  audience: BannerAudience;
  starts_at: string;
  ends_at: string | null;
  dismissible: boolean;
  priority: number;
}

const STALE_MS = 60_000;

/**
 * Resolve the banners-active endpoint URL. Order of resolution:
 *   1. process.env.SAZEO_BANNERS_API (Node / build-time)
 *   2. (globalThis as any).__SAZEO_BANNERS_API__ (runtime override)
 *   3. https://app-api.sazeo.io/functions/v1/banners-active (default prod)
 */
function resolveBannersEndpoint(): string {
  if (typeof process !== 'undefined' && process.env?.SAZEO_BANNERS_API) {
    return process.env.SAZEO_BANNERS_API;
  }
  // biome-ignore lint/suspicious/noExplicitAny: runtime config injection
  const override = (globalThis as any).__SAZEO_BANNERS_API__;
  if (typeof override === 'string' && override.length > 0) return override;
  return 'https://app-api.sazeo.io/functions/v1/banners-active';
}

/**
 * Fetch the currently-active banner for a slot from the public edge function.
 * Returns null when no banner is enabled / in-period for that slot.
 */
export function useBannerForSlot(slot: BannerSlot): UseQueryResult<ActiveBanner | null> {
  return useQuery({
    queryKey: ['banner-slot', slot],
    queryFn: async () => {
      const endpoint = resolveBannersEndpoint();
      const url = `${endpoint}?slot=${encodeURIComponent(slot)}`;
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) {
        // Render-pure: surface the error but don't throw at the slot boundary
        // upstream; query state already exposes isError to callers who want it.
        throw new Error(`banners-active fetch failed: ${res.status}`);
      }
      const json = (await res.json()) as { banner: ActiveBanner | null };
      return json.banner ?? null;
    },
    staleTime: STALE_MS,
    retry: 1,
  });
}
